import React, { useState, useEffect } from 'react';
import { Plus, List, Network } from 'lucide-react';
import { db, Task, Goal, Matter, Project, Client } from '../../lib/db';
import TaskForm from './TaskForm';
import TimeEntryModal from './TimeEntryModal';
import TaskDependencyGraph from './TaskDependencyGraph';
import { TaskSwitchDialog } from './TaskSwitchDialog';
import { toast } from 'sonner';
import { useTimer } from '../../contexts/TimerContext';
import { useLocalTimer } from '../../hooks/useLocalTimer';
import { useNavigate } from 'react-router-dom';
import { TaskListDesktop } from './TaskListDesktop';
import { TaskListMobile } from './TaskListMobile';

interface TaskListProps {
  goalId: string;
  goal?: Goal;
  matter?: Matter;
  project?: Project;
  client?: Client;
  dbUserId: string;
  onTimeEntryUpdate?: () => void;
  onTaskUpdate?: () => void;
  highlightTaskId?: string;
  shouldHighlightTask?: boolean;
}

function TaskList({ goalId, goal, matter, project, client, dbUserId, onTimeEntryUpdate, onTaskUpdate, highlightTaskId, shouldHighlightTask }: TaskListProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showTimeEntry, setShowTimeEntry] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskDetails, setShowTaskDetails] = useState<string | null>(null);
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'graph'>('all');
  const [taskEstimates, setTaskEstimates] = useState<Map<string, number>>(new Map());
  const [showTaskSwitchDialog, setShowTaskSwitchDialog] = useState(false);
  const [pendingSwitchTask, setPendingSwitchTask] = useState<Task | null>(null);
  const { timerState, stopTimer: stopGlobalTimer } = useTimer();
  const localTimer = useLocalTimer();
  const navigate = useNavigate();

  useEffect(() => {
    if (dbUserId) {
      loadTasks();
    }
  }, [goalId, dbUserId]);

  // Auto-expand task if it needs to be highlighted
  useEffect(() => {
    if (highlightTaskId && shouldHighlightTask && tasks.some(t => t.id === highlightTaskId)) {
      setShowTaskDetails(highlightTaskId);
      setExpandedTask(highlightTaskId);
    }
  }, [highlightTaskId, shouldHighlightTask, tasks]);

  const loadTasks = async () => {
    try {
      const taskData = await db.tasks.findByGoalId(goalId, dbUserId);
      setTasks(taskData);
      
      // Load duration estimates for all tasks
      if (taskData.length > 0) {
        const taskIdentifiers = taskData.map(t => ({
          title: t.title,
          description: t.description,
          goalTemplateId: goal?.template_id
        }));
        
        const estimates = await db.taskCompletionStatistics.getBatchEstimates(taskIdentifiers, dbUserId);
        setTaskEstimates(estimates);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const handleTaskCreate = async (task: Partial<Task>) => {
    const cleanTask = {
      ...task,
      start_date: task.start_date || undefined,
      deadline: task.deadline || undefined,
      goal_id: goalId,
      user_id: dbUserId,
      position: tasks.length,
      title: task.title || '',
    } as Omit<Task, 'id' | 'created_at' | 'updated_at'>;

    try {
      const newTask = await db.tasks.create(cleanTask);
      setTasks([...tasks, newTask]);
      toast.success('Task created successfully');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleTaskUpdate = async (id: string, updates: Partial<Task>) => {
    try {
      const currentTask = tasks.find(t => t.id === id);
      if (!currentTask) {
        toast.error('Task not found');
        return;
      }

      // Check if this is a regular task being completed
      if (currentTask.is_regular && updates.status === 'Complete') {
        console.log('[TaskList] Completing regular task:', {
          id: currentTask.id,
          title: currentTask.title,
          is_regular: currentTask.is_regular
        });
        
        // Use the specialized regular task completion method
        const updatedTask = await db.tasks.completeRegularTask(id, dbUserId);
        
        // Remove the completed task from the list (it shouldn't show in deliverable view)
        setTasks(tasks.filter(task => task.id !== id));
        toast.success('Task completed successfully');
        
        // Notify parent component of task update
        if (onTaskUpdate) {
          onTaskUpdate();
        }
        return;
      }

      // Remove dependencies from updates to avoid schema cache error
      const { ...updatesWithoutDependencies } = updates;

      // Clean up the updates object
      const cleanUpdates = {
        ...updatesWithoutDependencies,
        deadline: updates.deadline || undefined,
        start_date: updates.start_date || undefined,
        end_date: updates.end_date || undefined
      };

      const updatedTask = await db.tasks.update(id, dbUserId, cleanUpdates);
      setTasks(tasks.map(task => task.id === id ? updatedTask : task));
      toast.success('Task updated successfully');
      
      // Notify parent component of task update
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      console.error('Error in handleTaskUpdate:', error);
      toast.error('An unexpected error occurred while updating the task');
    }
  };

  const handleTaskDelete = async (id: string) => {
    // Check if timer is running for this task
    if ((timerState.isRunning && timerState.task?.id === id) || localTimer.isTimerRunningForTask(id)) {
      toast.error('Cannot delete task while timer is running. Please stop the timer first.');
      return;
    }

    if (!confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      const success = await db.tasks.delete(id, dbUserId);
      if (success) {
        await loadTasks(); // Reload to update tree structure
        setShowTaskDetails(null);
        setSelectedTask(null);
        toast.success('Task deleted successfully');
      } else {
        // Task exists but user doesn't have permission to delete it
        toast.error('Unable to delete task. You may not have permission to delete this task.');
        console.error('Task deletion failed - no rows affected. Task ID:', id, 'User ID:', dbUserId);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(`Failed to delete task: ${error.message || 'Unknown error'}`);
    }
  };


  const handleTaskReactivate = async (taskId: string) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;
      
      // Remove the deferred marker and set is_live to true
      const cleanedNotes = task.notes ? task.notes.replace(/\n?\[Deferred to Tomorrow\]/g, '') : '';
      
      await db.tasks.update(taskId, dbUserId, {
        is_live: true,
        notes: cleanedNotes || null
      });
      
      toast.success('Task reactivated');
      await loadTasks();
    } catch (error) {
      console.error('Error reactivating task:', error);
      toast.error('Failed to reactivate task');
    }
  };

  const handleTaskReorder = async (startIndex: number, endIndex: number) => {
    try {
      // Create a new array with the reordered tasks
      const newTasks = Array.from(tasks);
      const [removed] = newTasks.splice(startIndex, 1);
      newTasks.splice(endIndex, 0, removed);

      // Update the local state immediately for a smooth UI experience
      setTasks(newTasks);

      // Update positions in the database
      for (let i = 0; i < newTasks.length; i++) {
        await db.tasks.update(newTasks[i].id, dbUserId, { position: i });
      }
    } catch (error) {
      console.error('Error in handleTaskReorder:', error);
      toast.error('Failed to reorder tasks');
      await loadTasks();
    }
  };

  const startTimeEntry = async (task: Task) => {
    // If a timer is already running for a different task (global or local), prompt to switch
    const isGlobalRunning = timerState.isRunning && timerState.task?.id !== task.id;
    const isLocalRunning = localTimer.isRunning && localTimer.currentTaskId !== task.id;
    if (isGlobalRunning || isLocalRunning) {
      setPendingSwitchTask(task);
      setShowTaskSwitchDialog(true);
      return;
    }

    // Navigate to Focus Mode and let Focus Mode auto-start its timer
    navigate('/focus', { state: { loadTaskId: task.id } });

    // Ensure task has a start status
    if (!task.start_date) {
      const now = new Date();
      await handleTaskUpdate(task.id, {
        start_date: now,
        status: 'In Progress',
        task_type: 'In Progress',
        deadline: task.deadline
      });
    }
  };

  const handleTaskSwitch = async (notes: string) => {
    if (!pendingSwitchTask) return;

    try {
      // Prefer stopping the global Focus Mode timer if it's running
      if (timerState.isRunning && timerState.task?.id) {
        const stopped = stopGlobalTimer();
        if (stopped && timerState.task?.id) {
          await db.timeEntries.create({
            task_id: timerState.task.id,
            user_id: dbUserId,
            start_time: stopped.startTime,
            end_time: stopped.endTime,
            notes,
            break_minutes: 0
          });
          const totalMinutes = await db.timeEntries.getTotalMinutesForTask(timerState.task.id);
          await handleTaskUpdate(timerState.task.id, {
            time_spent: totalMinutes,
            time_spent_minutes: totalMinutes
          });
        }
      } else {
        // Fall back to the lightweight local timer if present
        const elapsedMs = localTimer.stopTimer();
        if (elapsedMs > 0 && localTimer.currentTaskId) {
          await db.timeEntries.create({
            task_id: localTimer.currentTaskId,
            user_id: dbUserId,
            start_time: new Date(Date.now() - elapsedMs),
            end_time: new Date(),
            notes,
            break_minutes: 0
          });
          const totalMinutes = await db.timeEntries.getTotalMinutesForTask(localTimer.currentTaskId);
          await handleTaskUpdate(localTimer.currentTaskId, {
            time_spent: totalMinutes,
            time_spent_minutes: totalMinutes
          });
        }
      }
    } finally {
      // Close dialog and navigate to focus with the selected task
      setShowTaskSwitchDialog(false);
      const taskToStart = pendingSwitchTask;
      setPendingSwitchTask(null);
      if (taskToStart) {
        await startTimeEntry(taskToStart);
        toast.success('Switched tasks');
      }
    }
  };

  const toggleMobileExpand = (taskId: string) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
  };

  const openTaskDetails = (task: Task) => {
    setSelectedTask(task);
    setTimeout(() => {
      setShowTaskDetails(task.id);
    }, 0);
  };

  const closeTaskDetails = () => {
    setShowTaskDetails(null);
    setSelectedTask(null);
  };


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'all'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <List className="w-4 h-4" />
              All Tasks
            </div>
          </button>
          <button
            onClick={() => setViewMode('graph')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'graph'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Network className="w-4 h-4" />
              Graph View
            </div>
          </button>
        </div>

        <div className="flex">
          <button
            onClick={() => setShowNewTaskForm(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </button>
        </div>
      </div>

      {viewMode === 'graph' ? (
        <TaskDependencyGraph
          tasks={tasks}
          onTaskClick={openTaskDetails}
        />
      ) : (
        <>
          {showNewTaskForm && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Task</h3>
              <TaskForm
                onSubmit={async (task) => {
                  await handleTaskCreate(task);
                  setShowNewTaskForm(false);
                }}
                onCancel={() => setShowNewTaskForm(false)}
                availableTasks={tasks}
              />
            </div>
          )}

          <div className="hidden md:block space-y-4">
            <TaskListDesktop
              tasks={tasks}
              taskEstimates={taskEstimates}
              highlightTaskId={highlightTaskId}
              shouldHighlightTask={shouldHighlightTask}
              showTaskDetails={showTaskDetails}
              selectedTask={selectedTask}
              onSelectTask={openTaskDetails}
              onCloseDetails={closeTaskDetails}
              onTaskUpdate={handleTaskUpdate}
              onTaskDelete={handleTaskDelete}
              onTimeEntry={(task) => { void startTimeEntry(task); }}
              onReactivate={handleTaskReactivate}
              onMakeLive={(taskId) => handleTaskUpdate(taskId, { is_live: true })}
            />
          </div>

          <TaskListMobile
            tasks={tasks}
            expandedTask={expandedTask}
            onToggleExpand={toggleMobileExpand}
            localTimer={localTimer}
            onStartTimeEntry={(task) => { void startTimeEntry(task); }}
            onTaskUpdate={handleTaskUpdate}
            onTaskDelete={handleTaskDelete}
            onTaskReactivate={handleTaskReactivate}
          />

          {!showNewTaskForm ? (
            <button
              onClick={() => setShowNewTaskForm(true)}
              className="w-full py-2 px-4 border border-gray-300 border-dashed rounded-lg text-gray-500 hover:text-gray-700 hover:border-gray-400 flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Task
            </button>
          ) : null}
        </>
      )}

      {showTimeEntry && selectedTask && (
        <TimeEntryModal
          task={selectedTask}
          goal={goal}
          matter={matter}
          project={project}
          client={client}
          dbUserId={dbUserId}
          onClose={() => {
            setShowTimeEntry(false);
            setSelectedTask(null);
          }}
          onTimeEntrySaved={() => {
            loadTasks();
            onTimeEntryUpdate?.();
          }}
        />
      )}

      {showTaskSwitchDialog && pendingSwitchTask && (
        <TaskSwitchDialog
          currentTaskTitle={localTimer.currentTaskTitle || 'Current task'}
          newTaskTitle={pendingSwitchTask.title}
          onConfirm={handleTaskSwitch}
          onCancel={() => {
            setShowTaskSwitchDialog(false);
            setPendingSwitchTask(null);
          }}
        />
      )}
    </div>
  );
}

export default TaskList;
